import React from 'react'
import styles from '../style.module.css'
import {
	Typography,
} from '@material-ui/core'
import { useRegistrationCertificatesStore } from '../stores/registrationCertificateStore'
import { useRegisteredImagesStore } from '../stores/registeredImagesStore'
import InfiniteScroll from 'react-infinite-scroll-component'
import clsx from 'clsx'
import Button from '../../../generic/Button'
import { UploadCertificateModal } from './UploadCertificateModal'
import { BulkUploadCertificatesModal } from './BulkUploadCertificatesModal'
import { getSelectedClients } from '../../../data/interactions'

export default function RegistrationCertificatesList(props) {
	const {
		hasRegistrationCertificatesInitiallyLoaded,
		fetchRegistrationCertificates,
		registrationCertificates,
		totalRegistrationCertificatesCount,
		loadError,
		setSearchString,
		searchString,
		setUploadCertificateModalOpen,
		setBulkUploadModalOpen,
	} = useRegistrationCertificatesStore()
	const { selectedRegistrationCertificate } = useRegisteredImagesStore()

	const selectedClient = getSelectedClients()[0]

	React.useEffect(() => {
		const loadInitialCertificates = async () => {
			// A link can carry a certificate number, so that a system that only
			// knows the number can still point straight at the certificate.
			const searchParam = new URLSearchParams(props.location.search).get(
				'search',
			)

			if (searchParam) {
				setSearchString(searchParam)
			}

			await fetchRegistrationCertificates({
				isInitialLoad: true,
			})

			if (
				!searchParam ||
				props.location.pathname !== '/registration-certificates'
			) {
				return
			}

			// Certificate numbers are only unique per partner, so open the
			// certificate directly only when the number resolves to exactly one.
			const matches =
				useRegistrationCertificatesStore.getState().registrationCertificates
			if (matches.length === 1) {
				selectCertificate(matches[0].registration_certificate_id)
			}
		}

		loadInitialCertificates()
	}, [])

	const handleKeyDown = async (event) => {
		if (event.key === 'Enter') {
			await handleSearch()
		}
	}

	const handleSearch = async () => {
		fetchRegistrationCertificates({
			isInitialLoad: true,
		})
	}

	const selectCertificate = (registrationCertificateId) => {
		props.history.push(
			`/registration-certificates/${registrationCertificateId}`,
		)
	}

	return (
		<div className={styles.subPageContainer}>
			<div className={styles.titleContainer}>
				<div>
					<Typography variant='body2' className={styles.listTitle}>
						Registration Certificates
					</Typography>
					<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
						{totalRegistrationCertificatesCount} registration certificates
						across all partners
					</Typography>
				</div>
				<div>
					<div style={{ display: 'flex', gap: '10px' }}>
						<Button onClick={() => setUploadCertificateModalOpen(true)}>
							Upload Certificate
						</Button>
						<Button onClick={() => setBulkUploadModalOpen(true)}>
							Bulk Upload CSV
						</Button>
					</div>
					{selectedClient && (
						<Typography
							variant='body2'
							fontColor='rgb(142, 147, 155)'
							style={{ textAlign: 'right' }}
						>
							Uploads are added to {selectedClient.name}
						</Typography>
					)}
				</div>
			</div>
			<div className={styles.searchContainer}>
				<input
					className={styles.searchInput}
					onKeyDown={handleKeyDown}
					value={searchString}
					onChange={(e) => setSearchString(e.target.value)}
					placeholder={'Search by certificate number or partner'}
				/>
				<Button
					style={{ marginLeft: '16px', marginBottom: 0 }}
					onClick={handleSearch}
				>
					Search for certificates
				</Button>
			</div>
			<div
				id='registrationCertificatesList'
				className={styles.listContainer}
				style={{ height: 'calc(100vh - 350px)' }}
			>
				{hasRegistrationCertificatesInitiallyLoaded ? (
					loadError && registrationCertificates.length === 0 ? (
						<div className={styles.loadingContainer}>
							<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
								{loadError}
							</Typography>
						</div>
					) : registrationCertificates.length > 0 ? (
						<InfiniteScroll
							className={styles.infiniteScrollWrap}
							dataLength={registrationCertificates.length}
							next={fetchRegistrationCertificates}
							hasMore={
								registrationCertificates.length <
								totalRegistrationCertificatesCount
							}
							loader={
								<div key={0} className={styles.loadingContainer}>
									Loading...
								</div>
							}
							scrollableTarget='registrationCertificatesList'
						>
							{registrationCertificates.map(
								(registrationCertificate, index) => (
									<div key={index}>
										<div
											className={clsx(
												styles.card,
												selectedRegistrationCertificate &&
													selectedRegistrationCertificate.registration_certificate_id ===
														registrationCertificate.registration_certificate_id &&
													styles.selectedCard,
											)}
											onClick={() =>
												selectCertificate(
													registrationCertificate.registration_certificate_id,
												)
											}
											style={{ display: 'flex' }}
										>
											<div
												className={styles.containerWithChip}
												style={{ width: '200px' }}
											>
												<div>
													<Typography variant='body2'>
														{
															registrationCertificate.registration_certificate_number
														}
													</Typography>
												</div>
											</div>
											<div className={styles.rowDivider} />
											<div
												className={styles.containerWithChip}
												style={{ width: '220px' }}
											>
												<div>
													<Typography
														variant='body2'
														fontColor='rgb(142, 147, 155)'
													>
														Partner
													</Typography>
													<Typography variant='body2'>
														{registrationCertificate.client_name ||
															'Unknown partner'}
													</Typography>
												</div>
											</div>
											<div className={styles.rowDivider} />
											<div
												className={styles.containerWithChip}
												style={{ width: '160px' }}
											>
												<div>
													<Typography variant='body2'>
														{registrationCertificate.registered_image_count}{' '}
														images
													</Typography>
												</div>
											</div>
											<div className={styles.rowDivider} />
											<div className={styles.containerWithChip}>
												<div>
													<Typography
														variant='body2'
														fontColor='rgb(142, 147, 155)'
													>
														Effective Date
													</Typography>
													<Typography variant='body2'>
														{
															registrationCertificate.effective_registration_date
														}
													</Typography>
												</div>
											</div>
										</div>
									</div>
								),
							)}
						</InfiniteScroll>
					) : (
						<div className={styles.loadingContainer}>
							<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
								No certificates found.
							</Typography>
						</div>
					)
				) : (
					<div className={styles.loadingContainer}>Loading...</div>
				)}
			</div>
			<UploadCertificateModal
				uploadSuccessCallback={(response) => {
					props.history.push(
						`/registration-certificates/${response.registration_certificate_id}`,
					)
				}}
			/>
			<BulkUploadCertificatesModal
				uploadSuccessCallback={() => {
					fetchRegistrationCertificates({
						isInitialLoad: true,
					})
				}}
			/>
		</div>
	)
}
